




























import { useI18N } from '@a/composables/use-vue-i18n';
import { useStore } from '@a/store';
import { CloudUnitType } from '@a/store/product/types';
import { useBaseModpackSearch } from '@a/component/landingpage/modpack/modpackSearchFeatures';
import {
  computed, defineComponent, onMounted, onUnmounted, reactive, watch,
} from '@vue/composition-api';
import { PropType } from 'vue';
import LoadingAnimation from '../../helper/LoadingAnimation.vue';
import FilterTextInput from '../../ui/text/FilterTextInput.vue';
import { Mod, ModTagGroup } from './types';

export default defineComponent({
  name: 'ModpackSearchInput',
  components: {
    LoadingAnimation, FilterTextInput,
  },
  emits: [
    'change',
    'loadingModsError',
  ],
  model: {
    prop: 'value',
    event: 'change',
  },
  props: {
    floatStyle: {
      type: Boolean,
      default: false,
    },
    isShowRam: {
      type: Boolean,
      default: false,
    },
    selectable: {
      type: Boolean,
      default: false,
    },
    ramUnitType: {
      type: Object as PropType<CloudUnitType>,
      default: null,
    },
    selectedModRepoKey: {
      type: String,
      default: null,
    },
    isValid: {
      type: Boolean,
      default: true,
    },
  },
  setup: (props, { emit }) => {
    const i18n = useI18N();
    const { dispatch, getters } = useStore();
    const {
      filterModsByPhrase, labelFunction, groupLabelFunction,
    } = useBaseModpackSearch(props, i18n, getters);
    const noItemsFoundMessage = i18n.t('general:messages.items_not_found', { items: i18n.t('general:non_special_texts.mod_plural') });

    const state = reactive({
      selectedMod: null,
      groupedMods: [] as Array<ModTagGroup>,
      modsReady: computed<boolean>(() => getters['game/isModsReady']),
      modsError: computed<boolean>(() => getters['game/isModsFailure']),
      placeholder: computed<string>(() => {
        if (getters['game/isModsLoading']) {
          return i18n.t('general:non_special_texts.loading');
        }

        if (getters['game/isModsFailure']) {
          return noItemsFoundMessage;
        }

        return i18n.t('landingpage:mods.search.placeholder');
      }),
    });

    const modSelected = (selectedMod: Mod) => {
      state.selectedMod = selectedMod;
      emit('change', selectedMod);
    };

    const setSelectedModByRepokey = (repoKey: string) => {
      if (repoKey != null && repoKey.length > 0) {
        const tags = state.groupedMods;

        for (let index = 0; index < tags.length; index++) {
          const modTagGroup = tags[index];
          const preSelectedMod = modTagGroup.mods.find((mod) => mod.key === repoKey);

          if (preSelectedMod != null) {
            state.selectedMod = preSelectedMod;
            emit('change', state.selectedMod);

            return;
          }
        }
      }

      if (state.groupedMods.length > 0) {
        // select first mod which is always mc vanilla
        state.selectedMod = state.groupedMods[0].mods[0];
        emit('change', state.selectedMod);
        return;
      }

      emit('change', null);
    };

    const propsSelectedModRepoKeyUnwatch = watch(
      () => props.selectedModRepoKey, setSelectedModByRepokey,
    );

    onMounted(async () => {
      try {
        dispatch('game/initWorker');
        await dispatch('game/fetchMods');
        await dispatch('game/groupModsByTags');
        state.groupedMods = getters['game/groupedMods'];
      } catch (error) {
        dispatch('game/terminateWorker');
        emit('loadingModsError');
      }

      setSelectedModByRepokey(props.selectedModRepoKey);
    });

    onUnmounted(() => {
      propsSelectedModRepoKeyUnwatch();
      dispatch('game/terminateWorker');
    });

    return {
      state,
      i18n,
      noItemsFoundMessage,
      labelFunction,
      groupLabelFunction,
      filterModsByPhrase,
      modSelected,
    };
  },
});
